import React from 'react';
import Collapsible from '../Collapsible/Collapsible';

import styles from '../../css/Projects.module.css';

const Work = () => {
  const contentRef = React.useRef(null);

  // Custom hook to hide separators if there is a line break
  React.useEffect(() => {
    const update = () => {
      document.querySelectorAll(`.${styles.pipeSeparator}`).forEach(sep => {
        const parent = sep.closest("p");
        const prev = parent?.previousElementSibling;
        if (!prev) return;

        sep.style.display = parent.offsetTop > prev.offsetTop ? "none" : "";
      });
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <main className="mainContainer">
      <div ref={contentRef} className={styles.contentContainer} style={{ paddingTop: "3rem" }}>
        <h1>WORK</h1>

        <div className={styles.projectMeta}>
          <p className={styles.contentDescription}>New Mode Apps</p>
          <p className={styles.contentDescription}><span className={styles.pipeSeparator} aria-hidden="true">|</span> consulting by Lawrence Luo</p>
        </div>

        <section className={styles.projectsContainer}>
          <div className={styles.projectCard}>
            <h2 className={styles.projectName}>CREASED FASHION LLC</h2>
            <div className={styles.projectMeta}>
              <p className={styles.projectRole}>Engineering Lead</p>
              <p className={styles.projectDates}><span className={styles.pipeSeparator} aria-hidden="true">|</span> May 2025 - Present</p>
            </div>
            <p className={styles.projectTech}>React Native, TanStack Query, TypeScript</p>

            <div>
              <Collapsible>
                <div className={styles.projectInfo}>
                  <div className={styles.projectInfoSection}>
                    <p className={styles.projectSubtitle}>Problem</p>
                    <p className={styles.projectBodyText}>The luxury sneaker community lacked a dedicated platform that effectively combined trusted resale marketplace features with genuine social engagement and content creation.</p>
                  </div>

                  <div className={styles.projectInfoSection}>
                    <p className={styles.projectSubtitle}>Tasks</p>
                    <ul>
                      <li>Leading a cross-functional team of 3 engineers to architect and build a comprehensive social marketplace app from the ground up.</li>
                      <li>Architected and implemented core social features including Stories, Clips, direct messaging, and customizable community feeds using server sent events via react-native-sse.</li>
                      <li>Established robust testing protocols and CI/CD pipelines to ensure seamless performance for high-stakes features like real-time drop alerts and rapid transactions.</li>
                    </ul>
                  </div>

                  <div className={styles.projectInfoSection}>
                    <p className={styles.projectSubtitle}>Impact</p>
                    <p className={styles.projectBodyText}>Delivering a production-ready MVP that empowers sneaker enthusiasts to connect, create content, and safely trade, successfully launching early creator/seller onboarding. To be released in the second quarter of 2026.</p>
                  </div>
                </div>
              </Collapsible>
            </div>
          </div>

          <div className={styles.projectCard}>
            <h2 className={styles.projectName}>STEALTH STARTUP (EdTech)</h2>
            <div className={styles.projectMeta}>
              <p className={styles.projectRole}>Senior Frontend Developer</p>
              <p className={styles.projectDates}><span className={styles.pipeSeparator} aria-hidden="true">|</span> January 2024 - December 2025</p>
            </div>
            <p className={styles.projectTech}>Next.js, OpenAI API, Google Cloud Platform</p>

            <Collapsible>
              <div className={styles.projectInfo}>
                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Problem</p>
                  <p className={styles.projectBodyText}>The company required engineering support across multiple research initiatives, specifically needing a behavioral research platform to study scrolling habits in short-form video apps and an automated grading tool to streamline computer science education.</p>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Tasks</p>
                  <ul>
                    <li>Behavioral Research Platform<br />
                      Built a mobile-first web app to measure user scrolling patterns in a YouTube shorts clone. Engineered custom swipe detection with CSS transforms and implemented YouTube iframe API workarounds to bypass mobile autoplay restrictions. Built an in-memory CSV export system for session data collection.</li>
                    <li>AI-Powered Autograder<br />
                      Created an automated grading tool leveraging the OpenAI API to provide AI-powered feedback on student code. Built features to allow instructors to organize assessments by student and assignment, streamlining the code review process.</li>
                  </ul>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Impact</p>
                  <p className={styles.projectBodyText}>Delivered two successful research tools: a functional behavioral platform that enabled data-driven analysis of user engagement, and an autograder that significantly reduced cognitive overhead for educators conducting code reviews.</p>
                </div>
              </div>
            </Collapsible>
          </div>

          <div className={styles.projectCard}>
            <h2 className={styles.projectName}>P.O.P. LLC</h2>
            <div className={styles.projectMeta}>
              <p className={styles.projectRole}>Lead Developer</p>
              <p className={styles.projectDates}><span className={styles.pipeSeparator} aria-hidden="true">|</span> June 2023 - December 2023</p>
            </div>
            <p className={styles.projectTech}>React, Firebase, Radix UI</p>

            <Collapsible>
              <div className={styles.projectInfo}>
                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Problem</p>
                  <p className={styles.projectBodyText}>Tasked with creating a platform for creating digital rememberance cards.</p>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Tasks</p>
                  <ul>
                    <li>Built a robust system for users to upload and display high-resolution images and longform text posts as digital tributes.</li>
                    <li>Designed UI/UX that prioritized ease of use, especially for elderly users.</li>
                  </ul>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Impact</p>
                  <p className={styles.projectBodyText}>Delivered a complete product enabling people to create digital memorial cards with rich multimedia content.</p>
                </div>
              </div>
            </Collapsible>
          </div>

          <div className={styles.projectCard}>
            <h2 className={styles.projectName}>MONIST INC. (formerly Monolog)</h2>
            <div className={styles.projectMeta}>
              <p className={styles.projectRole}>Engineering Lead</p>
              <p className={styles.projectDates}><span className={styles.pipeSeparator} aria-hidden="true">|</span> April 2022 - Jan 2023</p>
            </div>
            <p className={styles.projectTech}>Expo, Jest, React Testing Library</p>

            <Collapsible>
              <div className={styles.projectInfo}>
                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Problem</p>
                  <p className={styles.projectBodyText}>Building a performant, AI-driven video journaling platform with a seamless user experience. Existing solutions felt &quot;heavy&quot; or overly complex, leading to poor user retention and high abandonment during the recording process.</p>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Tasks</p>
                  <ul>
                    <li>Spearheaded a cross-functional team of 4 engineers to deliver an AI video-journaling platform with React Native, owning the end-to-end release process for iOS & Android and establishing an automated CI/CD pipeline to ensure stable deployments.</li>
                    <li>Partnered with UX to create innovative &quot;flow-state&quot; interfaces; implemented interactive, physics-based UI elements during recording to quiet cognitive interference and mitigate performance anxiety.</li>
                    <li>Implemented a production-ready video compression solution for lower-end devices.</li>
                  </ul>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Impact</p>
                  <p className={styles.projectBodyText}>Reduced measured session abandonment rates by 18% through innovative physics-based UI design, while delivering a stable, production-ready platform across iOS and Android.</p>
                </div>
              </div>
            </Collapsible>
          </div>

          <div className={styles.projectCard}>
            <h2 className={styles.projectName}>SOLPERHEROS LLC</h2>
            <div className={styles.projectMeta}>
              <p className={styles.projectRole}>Full Stack Web3 Developer</p>
              <p className={styles.projectDates}><span className={styles.pipeSeparator} aria-hidden="true">|</span> October 2021 - January 2022</p>
            </div>
            <p className={styles.projectTech}>Solana, Rust, Photoshop</p>

            <Collapsible>
              <div className={styles.projectInfo}>
                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Problem</p>
                  <p className={styles.projectBodyText}>Launching an NFT collection requiring generative art, blockchain smart contract deployment, and a user-friendly minting experience, all while ensuring security and trait rarity.</p>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Tasks</p>
                  <ul>
                    <li>Developed custom scripts to programmatically generate unique NFT layers and metadata from Photoshop assets, ensuring trait rarity and visual consistency.</li>
                    <li>Architected and deployed the Solana smart contracts (programs) to handle the minting logic, focusing on low-latency transactions and security.</li>
                    <li>Built the minting site, providing a seamless wallet-connection experience for users.</li>
                  </ul>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Impact</p>
                  <p className={styles.projectBodyText}>Delivered a complete end-to-end NFT infrastructure enabling the successful launch and minting of a unique digital art collection on Solana.</p>
                </div>
              </div>
            </Collapsible>
          </div>

          <div className={styles.projectCard}>
            <h2 className={styles.projectName}>AUTOMOTIVE RESEARCH FIRM</h2>
            <div className={styles.projectMeta}>
              <p className={styles.projectRole}>Technical Consultant</p>
              <p className={styles.projectDates}><span className={styles.pipeSeparator} aria-hidden="true">|</span> August 2020 - March 2021</p>
            </div>
            <p className={styles.projectTech}>Django, SQL, IndexedDB, Service Worker API</p>


            <Collapsible>
              <div className={styles.projectInfo}>
                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Problem</p>
                  <p className={styles.projectBodyText}>A manual, high-risk data pipeline using fragmented Excel sheets, leading to data corruption and days or even weeks of manual reconciliation.</p>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Tasks</p>
                  <ul>
                    <li>Designed a standardized SQL schema to consolidate years of legacy data, mapping highly technical, non-standardized measurements into a single source of truth.</li>
                    <li>Developed a Progressive Web App (PWA) using Service Workers and IndexedDB. This allowed researchers to capture high-fidelity data in warehouses without internet connectivity.</li>
                    <li>Engineered a custom sync manager and session-persistent state to prevent data loss during multi-hour evaluations, ensuring 100% data fidelity.</li>
                  </ul>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Impact</p>
                  <p className={styles.projectBodyText}>Eliminated the manual data entry bottleneck, reducing data compilation time from days to minutes and future-proofing the company&apos;s B2B research datasets.</p>
                </div>
              </div>
            </Collapsible>
          </div>

          <div className={styles.projectCard}>
            <h2 className={styles.projectName}>CORTEX, INC.</h2>
            <div className={styles.projectMeta}>
              <p className={styles.projectRole}>Full Stack Developer</p>
              <p className={styles.projectDates}><span className={styles.pipeSeparator} aria-hidden="true">|</span> July 2019 - May 2020</p>
            </div>
            <p className={styles.projectTech}>JavaScript, Node.js, React Native, Firebase, Express</p>

            <Collapsible>
              <div className={styles.projectInfo}>
                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Problem</p>
                  <p className={styles.projectBodyText}>A college consulting company wanted to build a mobile platform to serve educational content to their clients. They needed a solution built from scratch, requiring both rapid development and thorough testing.</p>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Tasks</p>
                  <ul>
                    <li>Engineered & shipped from concept to production, establishing the foundational codebase and creating a modular component library to accelerate future development.</li>
                    <li>Developed an automated engagement engine via notifications sent by Firebase Cloud Messaging (FCM) to drive consistent user interaction.</li>
                  </ul>
                </div>

                <div className={styles.projectInfoSection}>
                  <p className={styles.projectSubtitle}>Impact</p>
                  <p className={styles.projectBodyText}>Increased daily active usage by 20-30% as measured by Google Analytics, while establishing a scalable codebase for future feature development.</p>
                </div>
              </div>
            </Collapsible>
          </div>
        </section>
      </div>
    </main>
  )
}

export default Work;
